import { useEffect, useRef } from 'react';
import type { Post, ViewMode } from '../types';
import { SERVER_URL } from '@/config';

/**
 * Hook to sync widget state changes to the backend
 * Debounces updates to avoid excessive API calls
 */
export function useSessionSync(
  sessionId: string | undefined,
  posts: Post[] | null,
  viewMode: ViewMode
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousStateRef = useRef<string>('');

  useEffect(() => {
    // Skip if no session ID
    if (!sessionId) {
      return;
    }

    // Skip if no posts
    if (!posts || posts.length === 0) {
      return;
    }

    // Create a stable hash of current state
    const currentStateHash = JSON.stringify({
      posts: posts.map(p => ({
        id: p.id,
        date: p.date,
        time: p.time,
        text: p.text,
        platform: p.platform,
        topic: p.topic,
        status: p.status,
        order: p.order,
        attachments: p.attachments,
        pageName: p.pageName,
        username: p.username,
      })),
      viewMode,
    });

    // Skip if state hasn't changed
    if (currentStateHash === previousStateRef.current) {
      return;
    }

    previousStateRef.current = currentStateHash;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce: wait 2 seconds before syncing (increased for typing comfort)
    timeoutRef.current = setTimeout(async () => {
      console.log('[useSessionSync] State stabilized, syncing now...');

      try {
        const endpoint = `${SERVER_URL}/sessions/${sessionId}`;

        console.log(`[useSessionSync] Syncing to ${endpoint}...`, {
          postsCount: posts.length,
          viewMode,
        });

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            posts,
            viewMode,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[useSessionSync] Sync failed:', {
            status: response.status,
            error: errorData,
          });
          return;
        }

        const updatedSession = await response.json();
        console.log('[useSessionSync] ✅ Synced successfully:', {
          sessionId: updatedSession.sessionId,
          postsCount: updatedSession.posts.length,
        });
      } catch (error) {
        console.error('[useSessionSync] Sync error:', error);
      }
    }, 2000); // 2 second debounce

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [sessionId, posts, viewMode]);
}
